import React, { useEffect, useState, useCallback, useRef } from "react";
import { Plus, Loader2, Check, Download, Share2, ChevronLeft, PenLine } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";

const STATUS_LABEL = { in_progress: "In progress", completed: "Completed", reviewed: "Reviewed", draft: "Draft" };

function SignaturePad({ onCancel, onConfirm, saving }) {
  const canvasRef = useRef(null);
  const drawing = useRef(false);
  const [hasInk, setHasInk] = useState(false);
  const pos = (e) => {
    const c = canvasRef.current; const r = c.getBoundingClientRect();
    const t = e.touches ? e.touches[0] : e;
    return { x: (t.clientX - r.left) * (c.width / r.width), y: (t.clientY - r.top) * (c.height / r.height) };
  };
  const start = (e) => { e.preventDefault(); drawing.current = true; const ctx = canvasRef.current.getContext("2d"); ctx.lineWidth = 2.5; ctx.lineCap = "round"; ctx.strokeStyle = "#1D2421"; const p = pos(e); ctx.beginPath(); ctx.moveTo(p.x, p.y); };
  const move = (e) => { if (!drawing.current) return; e.preventDefault(); const ctx = canvasRef.current.getContext("2d"); const p = pos(e); ctx.lineTo(p.x, p.y); ctx.stroke(); setHasInk(true); };
  const end = () => { drawing.current = false; };
  const clear = () => { const c = canvasRef.current; c.getContext("2d").clearRect(0, 0, c.width, c.height); setHasInk(false); };
  const confirm = () => { if (!hasInk) { toast.error("Please sign in the box first"); return; } onConfirm(canvasRef.current.toDataURL("image/png")); };
  return (
    <div className="fixed inset-0 z-[80] bg-black/50 flex items-end sm:items-center justify-center p-0 sm:p-4" data-testid="signature-pad">
      <div className="bg-white w-full sm:max-w-md rounded-t-2xl sm:rounded-2xl p-5">
        <h3 className="font-serif text-xl text-forest mb-1 flex items-center gap-2"><PenLine size={18} className="text-rose" /> Sign to submit</h3>
        <p className="text-xs text-stone-500 mb-3">Sign below to confirm you completed this worksheet.</p>
        <canvas ref={canvasRef} width={600} height={220} data-testid="signature-canvas"
          onMouseDown={start} onMouseMove={move} onMouseUp={end} onMouseLeave={end}
          onTouchStart={start} onTouchMove={move} onTouchEnd={end}
          className="w-full h-40 border-2 border-dashed border-stone-300 rounded-lg touch-none bg-sand/30" />
        <div className="flex gap-2 mt-3">
          <button onClick={onCancel} data-testid="signature-cancel" className="flex-1 py-2.5 rounded-full bg-stone-200 text-forest font-semibold text-sm">Cancel</button>
          <button onClick={clear} data-testid="signature-clear" className="flex-1 py-2.5 rounded-full bg-sand text-forest font-semibold text-sm">Clear</button>
          <button onClick={confirm} disabled={saving} data-testid="signature-confirm" className="flex-1 py-2.5 rounded-full bg-rose text-white font-semibold text-sm disabled:opacity-60">{saving ? <Loader2 size={16} className="animate-spin mx-auto" /> : "Confirm"}</button>
        </div>
      </div>
    </div>
  );
}

export default function StaffWorksheets({ history = false }) {
  const [list, setList] = useState(null);
  const [active, setActive] = useState(null);
  const [saving, setSaving] = useState(false);
  const [creating, setCreating] = useState(false);
  const [prop, setProp] = useState("");
  const [signOpen, setSignOpen] = useState(false);

  const load = useCallback(() => api.get("/worksheet/my").then((r) => setList(r.data)).catch(() => setList([])), []);
  useEffect(() => { load(); }, [load]);

  const open = async (id) => {
    try { const { data } = await api.get(`/worksheet/worksheets/${id}`); setActive(data); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const create = async () => {
    if (!prop.trim()) { toast.error("Enter a property name"); return; }
    setCreating(true);
    try { const { data } = await api.post("/worksheet/worksheets", { property_name: prop.trim(), start_time: new Date().toTimeString().slice(0, 5) }); setProp(""); setActive(data); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setCreating(false); }
  };
  const toggle = (tid) => setActive((w) => ({ ...w, responses: { ...(w.responses || {}), [tid]: !(w.responses || {})[tid] } }));
  const total = active ? active.sections.reduce((n, s) => n + s.tasks.length, 0) : 0;
  const done = active ? Object.values(active.responses || {}).filter(Boolean).length : 0;
  const pct = total ? Math.round(done / total * 100) : 0;

  const save = async () => {
    setSaving(true);
    try { await api.patch(`/worksheet/worksheets/${active.id}`, { responses: active.responses || {} }); toast.success("Saved"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };
  const doSubmit = async (signature) => {
    setSaving(true);
    try {
      await api.patch(`/worksheet/worksheets/${active.id}`, { responses: active.responses || {}, end_time: new Date().toTimeString().slice(0, 5) });
      const { data } = await api.post(`/worksheet/worksheets/${active.id}/submit`, { signature });
      setSignOpen(false);
      toast.success(`Submitted — ${data.completion_percent}% complete`);
      const fresh = await api.get(`/worksheet/worksheets/${active.id}`); setActive(fresh.data); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };
  const downloadPdf = async (w) => {
    try { const r = await api.get(`/worksheet/worksheets/${w.id}/pdf`, { responseType: "blob" }); const u = URL.createObjectURL(r.data); const a = document.createElement("a"); a.href = u; a.download = `worksheet-${w.date}.pdf`; a.click(); setTimeout(() => URL.revokeObjectURL(u), 60000); }
    catch { toast.error("PDF unavailable"); }
  };
  const whatsapp = (w) => {
    downloadPdf(w);
    const msg = encodeURIComponent(`Cleaning worksheet — ${w.property_name} on ${w.date}: ${w.completion_percent}% complete. (PDF downloaded — please attach it here.)`);
    window.open(`https://wa.me/?text=${msg}`, "_blank");
  };

  if (active) {
    const submitted = active.status === "completed" || active.status === "reviewed";
    return (
      <div className="p-4" data-testid="staff-worksheet-editor">
        <button onClick={() => setActive(null)} className="text-sm text-stone-500 inline-flex items-center gap-1 mb-3"><ChevronLeft size={16} /> Back</button>
        <div className="card-lux p-4 mb-3">
          <div className="font-serif text-lg text-forest">{active.property_name}</div>
          <div className="text-xs text-stone-400">{active.date} · {STATUS_LABEL[active.status]}</div>
          {active.signed_at && <div className="text-xs text-green-700 mt-1 inline-flex items-center gap-1" data-testid="ws-signed"><PenLine size={12} /> Signed by {active.signed_by} · {(active.signed_at || "").slice(0, 10)}</div>}
          <div className="mt-3 h-2 bg-stone-100 rounded-full overflow-hidden"><div className="h-full bg-rose transition-all" style={{ width: `${pct}%` }} /></div>
          <div className="text-xs text-stone-500 mt-1" data-testid="ws-completion">{done}/{total} tasks · {pct}%</div>
        </div>
        {active.sections.map((s) => (
          <div key={s.title} className="card-lux p-4 mb-3">
            <h4 className="font-serif text-forest mb-2">{s.title}</h4>
            <div className="space-y-1">
              {s.tasks.map((t) => {
                const on = !!(active.responses || {})[t.id];
                return (
                  <button key={t.id} onClick={() => !submitted && toggle(t.id)} disabled={submitted} data-testid={`ws-task-${t.id}`}
                    className={`w-full flex items-center gap-2 text-left text-sm rounded-lg px-2.5 py-2 ${on ? "bg-green-50" : "bg-sand/40"}`}>
                    <span className={`w-5 h-5 rounded flex items-center justify-center flex-shrink-0 ${on ? "bg-green-600 text-white" : "border border-stone-300"}`}>{on && <Check size={13} />}</span>
                    <span>{t.name}</span>
                  </button>
                );
              })}
            </div>
          </div>
        ))}
        <div className="sticky bottom-16 flex gap-2 py-2">
          {!submitted ? (
            <>
              <button onClick={save} disabled={saving} data-testid="ws-save" className="flex-1 py-2.5 rounded-full bg-stone-200 text-forest font-semibold text-sm disabled:opacity-60">Save</button>
              <button onClick={() => setSignOpen(true)} disabled={saving} data-testid="ws-submit" className="flex-1 py-2.5 rounded-full bg-rose text-white font-semibold text-sm disabled:opacity-60 inline-flex items-center justify-center gap-1"><PenLine size={15} /> Sign & submit</button>
            </>
          ) : (
            <>
              <button onClick={() => downloadPdf(active)} data-testid="ws-download" className="flex-1 py-2.5 rounded-full bg-forest text-cream font-semibold text-sm inline-flex items-center justify-center gap-1"><Download size={15} /> PDF</button>
              <button onClick={() => whatsapp(active)} data-testid="ws-whatsapp" className="flex-1 py-2.5 rounded-full bg-green-600 text-white font-semibold text-sm inline-flex items-center justify-center gap-1"><Share2 size={15} /> WhatsApp</button>
            </>
          )}
        </div>
        {signOpen && <SignaturePad saving={saving} onCancel={() => setSignOpen(false)} onConfirm={doSubmit} />}
      </div>
    );
  }

  const rows = (list || []).filter((w) => history ? (w.status === "completed" || w.status === "reviewed") : true);
  return (
    <div className="p-4" data-testid={history ? "staff-history" : "staff-worksheets"}>
      {!history && (
        <div className="card-lux p-4 mb-4 flex items-end gap-2">
          <div className="flex-1"><label className="text-xs text-stone-500">New worksheet — property</label>
            <input value={prop} onChange={(e) => setProp(e.target.value)} data-testid="ws-new-property" className="w-full mt-1 rounded-lg border border-stone-200 px-3 py-2.5 text-base" placeholder="e.g. The Rose House" /></div>
          <button onClick={create} disabled={creating} data-testid="ws-create" className="btn-primary text-sm">{creating ? <Loader2 size={15} className="animate-spin" /> : <><Plus size={15} /> Start</>}</button>
        </div>
      )}
      {list === null ? <Loader2 className="animate-spin text-rose mx-auto my-8" /> : rows.length === 0 ? (
        <p className="text-center text-stone-400 py-10 text-sm">{history ? "No completed worksheets yet." : "No worksheets yet — start one above."}</p>
      ) : rows.map((w) => (
        <button key={w.id} onClick={() => open(w.id)} data-testid={`ws-item-${w.id}`} className="w-full card-lux p-4 mb-2 text-left flex items-center justify-between">
          <div><div className="font-medium text-forest">{w.property_name}</div><div className="text-xs text-stone-400">{w.date} · {STATUS_LABEL[w.status]}</div></div>
          <div className="text-right"><div className="font-serif text-xl text-rose">{w.completion_percent}%</div></div>
        </button>
      ))}
    </div>
  );
}
